import { Box, Flex, Heading, Icon as ChakraIcon, Image, Text, VStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FormattedMessage, useIntl } from 'react-intl';

import phoneMockup from 'assets/about-hero-phone.png';
import { CATEGORY_PILLS, PILL_POSITIONS, type CategoryPill } from './categoryPills';

/** Gentle vertical bob for the floating pills; disabled under prefers-reduced-motion. */
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const Pill = ({ pill, index }: { pill: CategoryPill; index: number }) => {
  const { Icon, surface, iconColor, textColor, rotate } = pill;
  return (
    // Wrapper owns the position + vertical bob; the inner pill owns the static tilt, so the
    // two transforms never overwrite each other.
    <Box
      position='absolute'
      {...PILL_POSITIONS[index]}
      zIndex={2}
      animation={`${float} 4s ease-in-out infinite`}
      sx={{
        animationDelay: `${index * 0.6}s`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Flex
        align='center'
        gap={{ base: '4px', md: '8px' }}
        bg={surface}
        color={textColor}
        borderRadius='9999px'
        px={{ base: '8px', md: '14px' }}
        py={{ base: '4px', md: '9px' }}
        boxShadow={{
          base: '0 4px 14px rgba(27, 25, 23, 0.12)',
          md: '0 8px 24px rgba(27, 25, 23, 0.12)',
        }}
        fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', sans-serif"
        fontWeight='700'
        fontSize={{ base: '10px', md: '14px' }}
        lineHeight='1'
        whiteSpace='nowrap'
        // Figma rotation is counter-clockwise-positive; CSS is clockwise-positive, so negate.
        transform={`rotate(${-rotate}deg)`}
      >
        {/* Text first → in RTL the icon sits on the trailing (left) side, matching the design. */}
        <FormattedMessage id={pill.labelId} defaultMessage={pill.defaultLabel} />
        <ChakraIcon
          as={Icon}
          color={iconColor}
          boxSize={{ base: '12px', md: '16px' }}
          strokeWidth={2.25}
          aria-hidden
        />
      </Flex>
    </Box>
  );
};

/**
 * About page hero (TZ-384): RTL eyebrow + headline + subtitle over a centered phone mockup
 * with floating pills (حفلات موسيقية، رياضة، مهرجانات، تجربة سلسة، ورشات، مسرح) arranged around
 * it on desktop and hugging the mockup on mobile. Matches the Figma "Hero" design.
 */
export const HeroSection = () => {
  const intl = useIntl();
  return (
    <Box
      as='section'
      dir='rtl'
      aria-labelledby='about-hero-heading'
      bg='white'
      // Soft warm glow at the top center fading into a white background, per the Figma hero.
      background='radial-gradient(120% 55% at 50% 0%, #FCEEEA 0%, #FFFFFF 55%)'
      overflow='hidden'
    >
      <VStack
        w='100%'
        maxW={{ base: '353px', md: 'container.xl' }}
        mx='auto'
        px={{ base: '16px', md: '100px' }}
        pt={{ base: '32px', md: '56px' }}
        pb={{ base: '48px', md: '72px' }}
        spacing={{ base: '12px', md: '16px' }}
        textAlign='center'
      >
        <Text
          as='p'
          m={0}
          color='dark.D700'
          fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', sans-serif"
          fontWeight='400'
          fontSize={{ base: '18px', md: '28px' }}
          lineHeight='115%'
          letterSpacing='-0.02em'
        >
          <FormattedMessage
            id='about.hero.eyebrow'
            defaultMessage='Because everyone has their own interests'
          />
        </Text>

        <Heading
          as='h1'
          id='about-hero-heading'
          m={0}
          maxW='720px'
          color='#1B1917' // text-primary
          fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', sans-serif"
          fontWeight='900'
          fontSize={{ base: '30px', md: '48px' }}
          lineHeight='110%'
          letterSpacing='0'
        >
          <FormattedMessage
            id='about.hero.headline'
            defaultMessage='We gathered all events in one place'
          />
        </Heading>

        <Text
          color='dark.D500'
          maxW='540px'
          fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', sans-serif"
          fontWeight='400'
          fontSize={{ base: '15px', md: '18px' }}
          lineHeight='160%'
          letterSpacing='0'
        >
          <FormattedMessage
            id='about.hero.subtitle'
            defaultMessage='A platform that brings together fans of concerts, sports and festivals in one easy and enjoyable experience.'
          />
        </Text>

        <Box
          position='relative'
          mt={{ base: '32px', md: '40px' }}
          mb={{ base: '8px', lg: '16px' }}
          mx='auto'
          w={{ base: '230px', md: '260px', lg: '270px' }}
        >
          {/* Soft neutral ground shadow under the phone. */}
          <Box
            aria-hidden
            position='absolute'
            bottom='-4%'
            left='8%'
            right='8%'
            h='14%'
            bg='rgba(27, 25, 23, 0.18)'
            filter='blur(28px)'
            borderRadius='9999px'
            zIndex={0}
          />
          <Image
            src={phoneMockup}
            alt={intl.formatMessage({
              id: 'about.hero.phoneAlt',
              defaultMessage: 'The Ticket Zone app showing events and concerts',
            })}
            position='relative'
            zIndex={1}
            w='100%'
            h='auto'
            loading='eager'
            draggable={false}
            sx={{ imageRendering: 'auto' }}
          />
          {CATEGORY_PILLS.map((pill, index) => (
            <Pill key={pill.id} pill={pill} index={index} />
          ))}
        </Box>
      </VStack>
    </Box>
  );
};
